(() => {
  const STORAGE_KEY = 'framewerk_shipping_postcode';
  const COUNTRY_STORAGE_KEY = 'framewerk_shipping_country';
  const ROOT = () => window.Shopify?.routes?.root || '/';
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function endpoint(path) {
    return `${ROOT()}${path}`.replace(/([^:]\/)\/+/g, '$1');
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {})
      },
      ...options
    });

    const text = await response.text();
    let data = null;

    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {
      data = null;
    }

    if (!response.ok) {
      throw new Error(
        data?.description || data?.message || text || `Shopify request failed (${response.status})`
      );
    }

    return data;
  }

  function stateForPostcode(postcode) {
    const p = Number(postcode);

    if (p >= 800 && p <= 999) return { name: 'Northern Territory', code: 'NT' };
    if ((p >= 200 && p <= 299) || (p >= 2600 && p <= 2618) || (p >= 2900 && p <= 2920)) return { name: 'Australian Capital Territory', code: 'ACT' };
    if ((p >= 1000 && p <= 2599) || (p >= 2619 && p <= 2899) || (p >= 2921 && p <= 2999)) return { name: 'New South Wales', code: 'NSW' };
    if (p >= 3000 && p <= 3999) return { name: 'Victoria', code: 'VIC' };
    if (p >= 4000 && p <= 4999) return { name: 'Queensland', code: 'QLD' };
    if (p >= 5000 && p <= 5799) return { name: 'South Australia', code: 'SA' };
    if (p >= 6000 && p <= 6797) return { name: 'Western Australia', code: 'WA' };
    if (p >= 7000 && p <= 7999) return { name: 'Tasmania', code: 'TAS' };

    return null;
  }

  // Restored exactly to the original working variant lookup.
  function selectedVariantId(container) {
    const section = container.closest('[id^="shopify-section-"]') || document;
    const input = section.querySelector('form[action*="/cart/add"] [name="id"]') || document.querySelector('form[action*="/cart/add"] [name="id"]');
    return Number(input?.value || container.dataset.variantId || 0);
  }

  function etaDateRange(minimumDate, maximumDate) {
    const format = new Intl.DateTimeFormat('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
    const minimum = new Date(`${minimumDate}T12:00:00`);
    const maximum = new Date(`${maximumDate}T12:00:00`);
    if (Number.isNaN(minimum.getTime()) || Number.isNaN(maximum.getTime())) {
      return 'Calculated at checkout';
    }
    return minimumDate === maximumDate
      ? format.format(minimum)
      : `${format.format(minimum)} – ${format.format(maximum)}`;
  }

  function handlingLabel(days) {
    if (!Number.isFinite(days)) return 'Calculated at checkout';
    if (days === 0) return 'Same business day';
    return `${days} business ${days === 1 ? 'day' : 'days'}`;
  }

  function money(value) {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return String(value || '');

    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  }

  function cheapestRate(rates, priceForRate) {
    return (Array.isArray(rates) ? rates : []).reduce((best, rate) => {
      const price = Number(priceForRate(rate));
      if (!Number.isFinite(price)) return best;
      if (!best || price < best.price) return { rate, price };
      return best;
    }, null);
  }

  // Original temporary-cart-line method retained for manual Shopify/Australia Post rates.
  async function addQuoteLine(variantId, quoteId) {
    const data = await requestJSON(endpoint('cart/add.js'), {
      method: 'POST',
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: { _framewerk_shipping_quote: quoteId }
        }]
      })
    });

    const line = data?.items?.find((item) => item.properties?._framewerk_shipping_quote === quoteId);
    if (!line?.key) throw new Error('Could not create the temporary shipping quote.');
    return line;
  }

  async function removeQuoteLine(lineKey) {
    await requestJSON(endpoint('cart/change.js'), {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
  }

  function addressParams(countryCode, postcode, state) {
    const country = countryCode === 'NZ' ? 'New Zealand' : 'Australia';
    const params = new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': country,
      'shipping_address[country_code]': countryCode
    });

    if (countryCode === 'AU' && state) {
      params.set('shipping_address[province]', state.name);
      params.set('shipping_address[province_code]', state.code);
    }

    return params;
  }

  // Restored exactly to the original working manual-rate request flow.
  async function calculateRates(countryCode, postcode, state) {
    const params = addressParams(countryCode, postcode, state);

    await requestJSON(`${endpoint('cart/prepare_shipping_rates.json')}?${params}`, {
      method: 'POST'
    });

    for (let attempt = 0; attempt < 12; attempt++) {
      await sleep(attempt === 0 ? 400 : 700);
      const result = await requestJSON(`${endpoint('cart/async_shipping_rates.json')}?${params}`);

      if (result?.shipping_rates) return result.shipping_rates;
    }

    throw new Error('Shipping rates took too long to calculate. Please try again.');
  }

  function getRememberedPostcode() {
    try {
      const postcode = localStorage.getItem(STORAGE_KEY) || '';
      return /^\d{4}$/.test(postcode) ? postcode : '';
    } catch (_) {
      return '';
    }
  }

  function rememberPostcode(postcode) {
    try {
      localStorage.setItem(STORAGE_KEY, postcode);
    } catch (_) {
      return;
    }
  }

  async function calculateFramewerkQuote(countryCode, postcode, totalWeightGrams) {
    return requestJSON(`${endpoint('apps/framewerk')}?resource=shipping-quote`, {
      method: 'POST',
      body: JSON.stringify({ countryCode, postcode, totalWeightGrams })
    });
  }

  function getRememberedCountry() {
    try {
      const countryCode = localStorage.getItem(COUNTRY_STORAGE_KEY) || 'AU';
      return countryCode === 'NZ' ? 'NZ' : 'AU';
    } catch (_) {
      return 'AU';
    }
  }

  function rememberCountry(countryCode) {
    try {
      localStorage.setItem(COUNTRY_STORAGE_KEY, countryCode);
    } catch (_) {
      return;
    }
  }

  function initialise(container) {
    if (container.dataset.framewerkShippingReady === 'true') return;
    container.dataset.framewerkShippingReady = 'true';

    const form = container.querySelector('[data-shipping-form]');
    const country = container.querySelector('[data-shipping-country]');
    const input = container.querySelector('[data-shipping-postcode]');
    const button = container.querySelector('[data-shipping-submit]');
    const changeButton = container.querySelector('[data-shipping-change]');
    const loading = container.querySelector('[data-shipping-loading]');
    const error = container.querySelector('[data-shipping-error]');
    const result = container.querySelector('[data-shipping-result]');
    const destination = container.querySelector('[data-shipping-destination]');
    const ratePrice = container.querySelector('[data-shipping-rate-price]');
    const origin = container.querySelector('[data-shipping-origin]');
    const handling = container.querySelector('[data-shipping-handling]');
    const eta = container.querySelector('[data-shipping-eta]');

    if (!country || !input || !button || !loading || !error || !result || !destination || !ratePrice || !origin || !handling || !eta) {
      console.error('Framewerk shipping calculator: required markup is missing.');
      return;
    }

    const rememberedPostcode = getRememberedPostcode();
    country.value = getRememberedCountry();
    if (rememberedPostcode) input.value = rememberedPostcode;

    let running = false;

    const showError = (message) => {
      error.textContent = message;
      error.hidden = false;
      result.hidden = true;
    };

    const run = async () => {
      if (running) return;

      const postcode = input.value.replace(/\D/g, '').slice(0, 4);
      const countryCode = country.value === 'NZ' ? 'NZ' : 'AU';
      const countryName = countryCode === 'NZ' ? 'New Zealand' : 'Australian';
      input.value = postcode;

      if (!/^\d{4}$/.test(postcode)) {
        showError(`Please enter a valid 4-digit ${countryName} postcode.`);
        return;
      }

      const state = countryCode === 'AU' ? stateForPostcode(postcode) : null;
      const variantId = selectedVariantId(container);

      if (countryCode === 'AU' && !state) {
        showError('That postcode could not be matched to an Australian state.');
        return;
      }

      if (!variantId) {
        showError('Please select a product option first.');
        return;
      }

      running = true;
      button.disabled = true;
      country.disabled = true;
      input.disabled = true;
      loading.hidden = false;
      error.hidden = true;
      result.hidden = true;

      const quoteId = `hc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      let quoteLineKey = null;

      try {
        const quoteLine = await addQuoteLine(variantId, quoteId);
        quoteLineKey = quoteLine.key;
        const [rates, framewerkQuote] = await Promise.all([
          calculateRates(countryCode, postcode, state),
          calculateFramewerkQuote(countryCode, postcode, Number(quoteLine.grams) || 0)
            .catch(() => ({ available: false, rates: [] }))
        ]);

        const shopifySelection = cheapestRate(rates, (rate) => rate.price);
        const framewerkSelection = framewerkQuote.available
          ? cheapestRate(framewerkQuote.rates, (rate) => Number(rate.priceMinor) / 100)
          : null;
        const shopifyRate = shopifySelection?.rate || null;
        const framewerkRate = framewerkSelection?.rate || null;

        if (!shopifyRate && !framewerkRate) {
          throw new Error('No shipping rates are available for this postcode.');
        }

        destination.textContent = `${countryCode === 'NZ' ? 'New Zealand' : 'Australia'} ${postcode}`;
        if (shopifyRate) {
          // Shopify's checkout-authoritative rates always win when available.
          // Framewerk estimates must never be combined with a different Shopify price.
          ratePrice.textContent = `From ${money(shopifySelection.price)}`;
          origin.textContent = 'Estimated for your current cart';
          handling.textContent = 'Confirmed at checkout';
          eta.textContent = 'Calculated at checkout';
        } else {
          ratePrice.textContent = `From ${money(framewerkSelection.price)}`;
          origin.textContent = framewerkRate.originLabel || 'Estimated shipping service';
          handling.textContent = handlingLabel(framewerkRate.handlingBusinessDays);
          eta.textContent = etaDateRange(
            framewerkRate.minimumDeliveryDate,
            framewerkRate.maximumDeliveryDate
          );
        }
        rememberPostcode(postcode);
        rememberCountry(countryCode);

        if (form) form.hidden = true;
        result.hidden = false;
      } catch (err) {
        console.error('Framewerk shipping calculator:', err);
        showError(err.message || 'Unable to calculate shipping. Please try again.');
      } finally {
        if (quoteLineKey) {
          try {
            await removeQuoteLine(quoteLineKey);
          } catch (cleanupError) {
            console.error('Could not remove temporary shipping quote:', cleanupError);
          }
        }

        loading.hidden = true;
        button.disabled = false;
        country.disabled = false;
        input.disabled = false;
        running = false;
      }
    };

    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 4);
    });

    country.addEventListener('change', () => {
      error.hidden = true;
      result.hidden = true;
      if (form) form.hidden = false;
      input.setAttribute(
        'aria-label',
        country.value === 'NZ' ? 'New Zealand postcode' : 'Australian postcode'
      );
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        run();
      }
    });

    button.addEventListener('click', run);

    if (changeButton) {
      changeButton.addEventListener('click', () => {
        result.hidden = true;
        error.hidden = true;
        if (form) form.hidden = false;
        input.focus();
        input.select();
      });
    }
  }

  function boot(root = document) {
    root.querySelectorAll('[data-framewerk-shipping]').forEach(initialise);
  }

  boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
